import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Button,
  Card,
  CardContent,
  Divider,
  Chip,
  Grid,
} from "@mui/material";
import { useStudents } from "hooks/useStudents";
import { useSchools } from "hooks/useSchools";
import { useAuth } from "../../contexts/AuthContext";
import { useSessionClass } from "../../contexts/SessionClassContext";
import { useStudentPhoto } from "../../hooks/useStudents";
import html2canvas from "html2canvas";
// @ts-ignore
import html2pdf from "html2pdf.js";
import { Download, Image, Person } from "@mui/icons-material";
import axios from "axios";
import { useTranslation } from "react-i18next";
// Material-UI styled components for better design

const AdmitCardTemplate = ({ data, hidePrintButton = false, hideStudentDetails = false, showAllSubjects = false, hideRollNumberAndPhoto = false }: { data: any; hidePrintButton?: boolean; hideStudentDetails?: boolean; showAllSubjects?: boolean; hideRollNumberAndPhoto?: boolean }) => {
  const { t } = useTranslation();
  const {
    user,
    isLoading: authLoading,
    isTeacher,
    isStudent,
    isSchoolAdmin,
    logout,
    isAdmin,
  } = useAuth();

  const { useStudentByEmail } = useStudents();
  const { data: currentStudent } = useStudentByEmail(
    isStudent() ? user?.email : undefined
  );

  // Get current session from context
  const { selectedSession } = useSessionClass();
  // Use current student data if available and no student data in props
  const examData = data.examSchedules || [];
  const student = data.student || currentStudent || {};
  const schoolFromData = data.school || {};

  // Fetch school data if not fully provided
  const { useSchoolById, useSchoolLogoInfo } = useSchools(false, { disableSchoolAdmins: true });
  const schoolId = schoolFromData.id || user?.schoolId;
  const { data: fetchedSchoolData } = useSchoolById(schoolId);
  const { data: logoInfo, isLoading: logoLoading } = useSchoolLogoInfo(schoolId);

  // Use fetched school data if available, otherwise use data.school
  const school = fetchedSchoolData || schoolFromData;

  // PDF and Image generation state
  const [isDownloadingImage, setIsDownloadingImage] = useState(false);
  const [isDownloadingPdfFromImage, setIsDownloadingPdfFromImage] = useState(false);
  const templateRef = useRef<HTMLDivElement>(null);

  const session = data.session || selectedSession || {};

  // Helper function to format date from string or array to DD-MM-YYYY
  const formatDisplayDate = (dateInput: any) => {
    if (!dateInput) return "";

    // Handle array format [year, month, day]
    if (Array.isArray(dateInput) && dateInput.length === 3) {
      const [year, month, day] = dateInput;
      const formattedDay = String(day).padStart(2, '0');
      const formattedMonth = String(month).padStart(2, '0');
      return `${formattedDay}-${formattedMonth}-${year}`;
    }

    // Handle string format (e.g., "2026-03-25")
    if (typeof dateInput === 'string') {
      // If it's already in DD-MM-YYYY format, return as is
      if (/^\d{2}-\d{2}-\d{4}$/.test(dateInput)) return dateInput;
      
      // If it's in YYYY-MM-DD format
      const match = dateInput.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        return `${match[3]}-${match[2]}-${match[1]}`;
      }
      return dateInput;
    }

    return "";
  };

  // Helper function to format date of birth
  const formatDateOfBirth = (dob: any) => {
    return formatDisplayDate(dob);
  };

  // Helper function to convert 24-hour time to 12-hour format with AM/PM
  // Examples: 14:00 -> 2:00 PM, 09:30 -> 9:30 AM, 00:15 -> 12:15 AM, 12:45 -> 12:45 PM    
  const convertTo12HourFormat = (hour: number, minute: number): string => {
    if (hour === 0) {
      return `12:${String(minute).padStart(2, '0')} AM`;
    } else if (hour < 12) {
      return `${hour}:${String(minute).padStart(2, '0')} AM`;
    } else if (hour === 12) {
      return `12:${String(minute).padStart(2, '0')} PM`;
    } else {
      return `${hour - 12}:${String(minute).padStart(2, '0')} PM`;
    }
  };

  // Helper function to parse time from various formats (array, string, etc.)
  // Supports: [14, 0] -> {hour: 14, minute: 0}, "14:00" -> {hour: 14, minute: 0}
  const parseTime = (timeInput: any): { hour: number; minute: number } | null => {
    if (!timeInput) return null;

    // Handle array format [hour, minute]
    if (Array.isArray(timeInput) && timeInput.length === 2) {
      const [hour, minute] = timeInput;
      if (typeof hour === 'number' && typeof minute === 'number') {
        return { hour, minute };
      }
    }

    // Handle string format "14:00" or "14:30"
    if (typeof timeInput === 'string') {
      const timeMatch = timeInput.match(/^(\d{1,2}):(\d{2})$/);
      if (timeMatch) {
        const hour = parseInt(timeMatch[1], 10);
        const minute = parseInt(timeMatch[2], 10);
        if (hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
          return { hour, minute };
        }
      }
    }

    return null;
  };

  // Helper function to format time from various formats - now with 12-hour format

  const formatTimeFromArray = (timeInput: any) => {
    const parsed = parseTime(timeInput);
    if (!parsed) {
      return "";
    }

    const { hour, minute } = parsed;
    return convertTo12HourFormat(hour, minute);
  };

  // Helper function to format time range from arrays - now with 12-hour format
  const formatTimeRangeFromArrays = (startTime: any, endTime: any) => {
    if (!startTime || !endTime) return "";

    const formattedStart = formatTimeFromArray(startTime);
    const formattedEnd = formatTimeFromArray(endTime);

    if (!formattedStart || !formattedEnd) return "";

    return `${formattedStart} - ${formattedEnd}`;
  };

  // Use current student data as fallback
  const rollNo = student.rollNumber || data.rollNumber || "";
  const className = student.className || data.className || "";
  const section = student.section || data.section || "";
  const enrolmentNo = student.enrolmentNo || data.enrolmentNo || "";
  // Fix student name extraction - prioritize studentName field from the new data structure
  const studentName = student.studentName ||
    (student.firstName && student.lastName ? `${student.firstName} ${student.lastName}` : "") ||
    data.studentName || "";
  const fatherName = student.fatherName || data.fatherName || "";
  const motherName = student.motherName || data.motherName || "";
  const dob = formatDateOfBirth(student.dateOfBirth || data.dateOfBirth);
  const gender = student.gender || data.gender || "";
  const medium = student.medium || data.medium || "ENGLISH";
  const caste = student.caste || data.caste || "";
  const schoolName = school.nameofSchool || data.schoolName || "GOVT H.S. SCHOOL VIJAYNAGAR INDORE";
  const schoolCode = school.schoolCode || data.schoolCode || "531062";
  const diceCode = school.diseCode || data.diseCode || "";
  const schoolAddress = `${school.block || ""}, ${school.district || ""}, ${school.state || ""} - ${school.pinNo || ""}`.replace(/^,\s*|,\s*$/g, '').replace(/,\s*,/g, ',') || "Vijaynagar, Indore, Madhya Pradesh";
  const principalName = school.schoolPrincipalName || data.principalName || "Dr. Rajesh Kumar Sharma";
  const principalDesignation = school.principalDesignation || data.principalDesignation || "Principal";
  const principalMobile = school.principalMobile || data.principalMobile || "";
  const principalEmail = school.principalEmail || data.principalEmail || "";
  const schoolMedium = school.medium || data.schoolMedium || "ENGLISH";
  const venue = data.venue || school.venue || data.center || school.center || "";
  // Photo functionality using the same approach as AadharCardTemplate
  const studentId = student.id || data.studentId;

  // Check if we have a pre-fetched photo URL (for bulk download)
  const preFetchedPhotoUrl = student.photoUrl || data.student?.photoUrl;

  // Use pre-fetched photo if available, otherwise use the hook
  const { imageUrl: authenticatedImageUrl, isLoading: photoLoading, error: imageError, hasPhoto } = useStudentPhoto(studentId);

  // Determine which photo URL to use
  const finalImageUrl = preFetchedPhotoUrl || authenticatedImageUrl;
  const finalPhotoLoading = preFetchedPhotoUrl ? false : photoLoading;
  const finalImageError = preFetchedPhotoUrl ? false : imageError;
  const finalHasPhoto = !!finalImageUrl && !finalImageError;

  const year = data.year || data.examYear || "";
  const examType = data.examType || t("annual_examination");
  const allIssuedSubjects = Array.isArray(examData) ? examData : [];

  // Filter subjects based on student's enrolled subjects
  const getStudentEnrolledSubjects = () => {
    if (!student || !student.subjects) return [];

    // Handle different subject data formats
    if (Array.isArray(student.subjects)) {
      return student.subjects;
    }

    // Handle comma-separated string
    if (typeof student.subjects === 'string') {
      return student.subjects.split(',').map((s: string) => s.trim()).filter(Boolean);
    }

    return [];
  };

  const studentEnrolledSubjects = getStudentEnrolledSubjects();

  // Filter exam schedules based on showAllSubjects prop and student enrollment
  const filteredSubjects = allIssuedSubjects.filter((schedule: any) => {
    if (!schedule.subjectCode) return false;

    // If showAllSubjects is true (eye icon view), show all subjects
    if (showAllSubjects) {
      return true;
    }

    // If no student data or no enrolled subjects, show all subjects (for template preview)
    if (!student || studentEnrolledSubjects.length === 0) {
      return true;
    }

    // Check if student is enrolled in this subject
    const isEnrolled = studentEnrolledSubjects.includes(schedule.subjectCode.toString());

    return isEnrolled;
  });

  // Sort subjects by exam date in ascending order
  const issuedSubjects = filteredSubjects.sort((a: any, b: any) => {
    const dateA = new Date(a.examDate);
    const dateB = new Date(b.examDate);
    return dateA.getTime() - dateB.getTime();
  });

  // Get session name with fallback
  const sessionName =
    session.name || selectedSession?.name || t("current_session");

  // Logo is now handled by the logo manager


  // Function to download template as image
  const downloadAsImage = async () => {
    if (!templateRef.current) return;

    setIsDownloadingImage(true);
    try {
      const canvas = await html2canvas(templateRef.current, {
        useCORS: true,
        allowTaint: true,
        background: '#ffffff',
        logging: false,
      });

      const link = document.createElement('a');
      link.download = `admit-card-${rollNo}-${examType}-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      alert(t('failed_download_image'));
    } finally {
      setIsDownloadingImage(false);
    }
  };

  // Function to download template as PDF from image
  const downloadAsPdfFromImage = async () => {
    if (!templateRef.current) return;

    setIsDownloadingPdfFromImage(true);
    try {
      const element = templateRef.current;
      const opt = {
        margin: [0.1, 0.1, 0.1, 0.1],
        filename: `admit-card-${rollNo}-${examType}-${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false,
        },
        jsPDF: {
          unit: 'in',
          format: 'a4',
          orientation: 'portrait',
          compress: true,
        },
        pagebreak: { mode: 'avoid-all' }
      };

      await html2pdf().set(opt).from(element).save();
    } catch (error) {
      alert(t('failed_download_pdf'));
    } finally {
      setIsDownloadingPdfFromImage(false);
    }
  };

  return (
    <div>
      <Card
        ref={templateRef}
        sx={{
          maxWidth: 800,
          margin: "32px auto",
          border: "2px solid #e0e0e0",
          borderRadius: 1,
          overflow: "hidden",
          boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          fontFamily: "Arial, sans-serif",
          position: "relative",
          background: "white",
        }}
      >
        {/* Watermark Background */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.05,
            zIndex: 0,
            "&::before": {
              content: `"${schoolName}"`,
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%) rotate(-45deg)",
              fontSize: "80px",
              fontWeight: "bold",
              color: "rgba(0,0,0,0.1)",
              whiteSpace: "nowrap",
              zIndex: 1,
            }
          }}
        />

        {/* Header Section with Logo and School Info */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            px: 3,
            py: 1,
            gap: 2,
            position: "relative",
            zIndex: 2,
          }}
        >
          {/* School Logo */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              border: "3px solid #fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              overflow: "hidden",
              background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
            }}
          >
            {logoLoading ? (
              <CircularProgress
                size={24}
                sx={{ color: "white" }}
              />
            ) : logoInfo && logoInfo.fileName ? (
              <img
                src={`${process.env.REACT_APP_API_URL}/schools/logos/${logoInfo.fileName}`}
                alt={`${schoolName} Logo`}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "50%",
                }}
                onLoad={() => {
                }}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = "none";
                  const parent = target.parentElement;
                  if (parent) {
                    parent.innerHTML = `
                       <div style="
                         color: white;
                         font-weight: bold;
                         font-size: 12px;
                         text-align: center;
                         line-height: 1.2;
                         display: flex;
                         align-items: center;
                         justify-content: center;
                         width: 100%;
                         height: 100%;
                       ">
                         ${schoolName.split(' ').map((word: string) => word.charAt(0)).join('').substring(0, 3)}
       </div>
                     `;
                  }
                }}
              />
            ) : (
              <Typography
                variant="h6"
                sx={{
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "12px",
                  textAlign: "center",
                  lineHeight: 1.2,
                }}
              >
                {schoolName.split(' ').map((word: string) => word.charAt(0)).join('').substring(0, 3)}
              </Typography>
            )}
          </Box>

          {/* School Information */}
          <Box sx={{ flex: 1, textAlign: "center" }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                fontSize: "30px",
                color: "#000",
                mb: 1,
                textTransform: "uppercase",
                textAlign: "center",
              }}
            >
              {schoolName}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                fontSize: "18px",
                color: "#666",
                mb: 2,
                textAlign: "center",
              }}
            >
              {schoolAddress}
            </Typography>
          </Box>
        </Box>

        {/* Address Bar */}
        <Box
          sx={{
            background: "#424242",
            color: "white",
            py: 0.1,
            textAlign: "center",
            position: "relative",
            zIndex: 2,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: "bold",
              fontSize: "14px",
            }}
          >

          </Typography>
        </Box>

        {/* Admit Card Title Chip */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            position: "relative",
            zIndex: 2,
          }}
        >
          <Box
            sx={{

              color: "black",
              px: 1,
              py: 1,
              fontWeight: "bold",
              fontSize: "26px",
              textTransform: "uppercase",
              letterSpacing: "1px",
            }}
          >

            {t("admit_card")}
          </Box>
        </Box>


        {/* Header Section with Roll No, Board/Class/Time, and Student Photo */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
            position: "relative",
            zIndex: 2,
            px: 3,
          }}
        >
          {/* Left side - Roll No - Only show if not hidden */}
          {!hideRollNumberAndPhoto && (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                pb: 1
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "bold",
                  fontSize: "18px",
                  color: "#000",
                  mt: 13,
                }}
              >
                {t("roll_no")}: {rollNo}
              </Typography>
            </Box>
          )}

          {/* Center - Board, Class, Time */}
          <Box sx={{ flex: hideRollNumberAndPhoto ? 1 : 2, textAlign: "center", mt: -2 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", fontSize: "22px" }}
            >
              {`${examType || t("annual")} ${t("EXAMINATION")}`}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{ fontSize: "18px", marginTop: "4px" }}
            >
              {sessionName || "2025-26"}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                fontSize: "16px",
                color: "#1976d2",
                mt: 1,
              }}
            >
              {t("class")} {className} {section}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                fontSize: "16px",
                color: "#000",
                mt: 1,
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: "18px",
                  fontWeight: "bold", // optional, if you want it bold
                }}
              >
                {t("Time")}: {formatTimeRangeFromArrays(data.startTime, data.endTime)}
              </Typography>
            </Typography>
          </Box>

          {/* Right side - Student Photo - Only show if not hidden */}
          {!hideRollNumberAndPhoto && (
            <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
              <Box
                sx={{
                  width: "150px",
                  height: "170px",
                  border: "2px solid #000",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  backgroundColor: "#fff",
                  textAlign: "center",
                  overflow: "hidden",
                  flexShrink: 0,
                  marginTop: "-50px",
                }}
              >
                {(() => {
                  if (finalPhotoLoading) {
                    return (
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }}>
                        <CircularProgress size={20} />
                        <Typography sx={{ fontSize: "8px", marginTop: "4px" }}>{t("loading")}...</Typography>
                      </Box>
                    );
                  }
                  if (finalImageUrl && !finalImageError) {
                    return (
                      <img
                        src={finalImageUrl}
                        alt={t("student_photo")}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    );
                  }

                  return (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: "#666" }}>
                      <Person sx={{ fontSize: 30, color: "#ccc", marginBottom: "2px" }} />
                      <Typography
                        sx={{
                          fontSize: "8px",
                          fontWeight: "bold",
                          lineHeight: 1.2,
                          marginBottom: "1px",
                        }}
                      >
                        {t("student_photo")}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "6px",
                          color: "#666",
                          lineHeight: 1.2,
                        }}
                      >
                        (Passport Size)
                      </Typography>
                    </Box>
                  );
                })()}
              </Box>
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: "bold", fontSize: "18px", mt: 1, pl: 3, pr: 3 }}>
          {venue && (
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                fontSize: "18px",
                color: "#000",
                mt: 1,
              }}
            >
              {t("Center")}: {venue}
            </Typography>
          )}
          {diceCode && (
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                fontSize: "18px",
                color: "#000",
                mt: 1,
              }}
            >
              {t("Dise Code")}: {diceCode}
            </Typography>
          )}
        </Box>

        <CardContent sx={{ px: 3, py: 1, position: "relative", zIndex: 2 }}>
          {/* Student Details Section */}
          {!hideStudentDetails && (
            <Box sx={{ mb: 2 }}>

              {/* Student Details Table */}
              <TableContainer
                component={Paper}
                sx={{
                  mb: 3,
                  border: "1px solid #ddd",
                  borderRadius: 1,
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ background: "linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)" }}>
                      <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>{t("enrolment_no")}</TableCell>
                      <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>{t("student_name")}</TableCell>
                      <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>{t("father_name")}</TableCell>
                      <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>{t("mother_name")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ textAlign: "center", fontWeight: "bold" }}>{enrolmentNo}</TableCell>
                      <TableCell sx={{ textAlign: "center", fontWeight: "bold" }}>{studentName}</TableCell>
                      <TableCell sx={{ textAlign: "center", fontWeight: "bold" }}>{fatherName}</TableCell>
                      <TableCell sx={{ textAlign: "center", fontWeight: "bold" }}>{motherName}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Personal Details Table */}
              {/* <TableContainer
                component={Paper}
                sx={{
                  mb: 3,
                  border: "1px solid #ddd",
                  borderRadius: 1,
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ background: "linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)" }}>
                      <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>DATE OF BIRTH</TableCell>
                      <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>M/F</TableCell>
                      <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>MEDIUM</TableCell>
                      <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>CASTE</TableCell>
                    </TableRow>
                  </TableHead>
				  <TableBody>
                    <TableRow>
                      <TableCell sx={{ textAlign: "center", fontWeight: "bold" }}>{dob}</TableCell>
                      <TableCell sx={{ textAlign: "center", fontWeight: "bold" }}>{gender}</TableCell>
                      <TableCell sx={{ textAlign: "center", fontWeight: "bold" }}>{medium}</TableCell>
                      <TableCell sx={{ textAlign: "center", fontWeight: "bold" }}>{caste}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer> */}
            </Box>
          )}

          {/* Subject Schedule Table */}
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      background: "linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)",
                      color: "#000",
                      fontWeight: "bold",
                      textAlign: "center",
                      fontSize: "16px",
                    }}
                  >
                    {t("s_no")}
                  </TableCell>
                  <TableCell
                    sx={{
                      background: "linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)",
                      color: "#000",
                      fontWeight: "bold",
                      textAlign: "center",
                      fontSize: "16px",
                    }}
                  >
                    {t("subject")}
                  </TableCell>
                  <TableCell
                    sx={{
                      background: "linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)",
                      color: "#000",
                      fontWeight: "bold",
                      textAlign: "center",
                      fontSize: "16px",
                    }}
                  >
                    {t("subject_code")}
                  </TableCell>
                  <TableCell
                    sx={{
                      background: "linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)",
                      color: "#000",
                      fontWeight: "bold",
                      textAlign: "center",
                      fontSize: "16px",
                    }}
                  >
                    {t("day")}
                  </TableCell>
                  <TableCell
                    sx={{
                      background: "linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)",
                      color: "#000",
                      fontWeight: "bold",
                      textAlign: "center",
                      fontSize: "16px",
                    }}
                  >
                    {t("date")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {issuedSubjects.map((schedule: any, index: number) => (
                  <TableRow key={index} hover>
                    <TableCell sx={{ textAlign: "center", fontSize: "15px" }}>
                      {index + 1}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "15px" }}>
                      {schedule.subjectName}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "15px" }}>
                      {schedule.subjectCode}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center", fontSize: "15px" }}>
                      {schedule.day}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontSize: "15px",
                        color: "#1976d2",
                        fontWeight: "bold"
                      }}
                    >
                      {formatDisplayDate(schedule.examDate)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Signature Section */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mt: 4,
              mb: 2,
            }}
          >
            <Box sx={{ textAlign: "center", flex: 1 }}>
              <Box
                sx={{
                  borderBottom: "2px dotted #000",
                  width: "150px",
                  height: "40px",
                  mx: "auto",
                  mb: 1,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  fontSize: "12px",
                  color: "#000",
                }}
              >
                {t("sign_of_exam_in_charge")}
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center", flex: 1 }}>
              <Box
                sx={{
                  borderBottom: "2px dotted #000",
                  width: "150px",
                  height: "40px",
                  mx: "auto",
                  mb: 1,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  fontSize: "12px",
                  color: "#000",
                }}
              >
                {principalName}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  fontSize: "12px",
                  color: "#000",
                }}
              >
                {t("sign_of_the_principal")}
              </Typography>
            </Box>
          </Box>

          {/* Instructions Section */}
          <Box
            sx={{
              borderRadius: 0,
              p: 3,
              mb: 3,
              textAlign: "left",
              background: "white",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                fontSize: "12px",
                color: "#000",
                textAlign: "start",
                textTransform: "uppercase",
                mb: 1,
              }}
            >
              {t("important_instructions_for_candidates")}
            </Typography>

            <Box
              sx={{
                width: "100%",
                height: "1px",
                backgroundColor: "#000",
                mb: 2,
              }}
            />

            <Typography
              variant="body1"
              sx={{
                fontSize: "12px",
                color: "#000",
                lineHeight: 1,
                whiteSpace: "pre-line",
              }}
            >
              {data.instructions || t("bring_admit_card_exam_hall")}
            </Typography>
          </Box>

        </CardContent>
      </Card>


      {/* Download Buttons - Only show if not hidden */}
      {!hidePrintButton && (
        <Box sx={{ marginTop: "16px", textAlign: "center" }}>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
            {/* Image Download Button */}
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Image />}
              disabled={isDownloadingImage || isDownloadingPdfFromImage}
              onClick={async () => {
                await downloadAsImage();
                await downloadAsPdfFromImage();
              }}
              sx={{
                padding: "12px 24px",
                fontSize: 14,
                fontWeight: 600,
                minWidth: "200px",
                position: "relative",
                backgroundColor: "#9c27b0",
                "&:hover": {
                  backgroundColor: "#7b1fa2",
                },
              }}
            >
              {(isDownloadingImage || isDownloadingPdfFromImage) ? (
                <>
                  <Box
                    sx={{
                      display: "inline-block",
                      width: "16px",
                      height: "16px",
                      border: "2px solid #fff",
                      borderTop: "2px solid transparent",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                      marginRight: "8px",
                      "@keyframes spin": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" },
                      },
                    }}
                  />
                  {isDownloadingImage ? t("generating_image") : t("generating_pdf_from_image")}
                </>
              ) : t("download_as_image_pdf")}
            </Button>
          </Box>

          {/* Loading progress indicators */}
          {(isDownloadingImage || isDownloadingPdfFromImage) && (
            <Box sx={{ marginTop: "12px" }}>
              <Typography variant="body2" color="text.secondary">
                {isDownloadingImage
                  ? t("please_wait_generating_admit_card_image")
                  : t("please_wait_generating_admit_card_pdf")
                }
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </div>
  );
};

export default AdmitCardTemplate;